import { Bell, Building, ChevronDown, LogOut, Settings, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore.js";
import { getDisplayAvatarChar } from "../../utils/helpers";
import DashboardSidebar from "../layout/DashboardSidebar";

// ─── Top Bar ──────────────────────────────────────────────────────────────────
const DashboardTopBar = ({ onMobileMenuToggle }) => {
    const user = useAuthStore(s => s.user);
    const logout = useAuthStore(s => s.logout);
    const navigate = useNavigate();
    const [profileOpen, setProfileOpen] = useState(false);
    const profileRef = useRef(null);

    // const { data: user } = useQuery({
    //     queryKey: ["profile"],
    //     queryFn: async () => {
    //         const res = await authServiceApi.getProfile();
    //         return res.data;
    //     },
    // });

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setProfileOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const backendUrl = import.meta.env.VITE_BACKEND_DOMAIN || "http://localhost:8000";
    const mediaUrl = user?.company_logo || user?.profile_picture;
    const hasDomain = mediaUrl?.startsWith("http")
    const avatarSrc = hasDomain ? mediaUrl : mediaUrl ? backendUrl + mediaUrl : null;

    const role = user?.role

    const displayName =
        role === "recruiter"
            ? user?.company_name || `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim()
            : user?.full_name || `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim();

    const profileLink = role === "recruiter" ? "/company-profile" : "/manage-profile";

    const handleLogout = () => {
        logout();
        navigate("/signin");
        setProfileOpen(false);
    };

    return (
        <header className="fixed top-0 left-0 right-0 z-50 h-14 bg-[#2563eb] border-b border-[#1d4ed8] shadow-sm flex items-center px-4 gap-3">
            {/* Mobile hamburger */}
            <button
                className="lg:hidden p-2 rounded-lg text-blue-200 hover:bg-white/10 flex-shrink-0"
                onClick={onMobileMenuToggle}
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
                </svg>
            </button>

            {/* Logo */}
            <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0"
            >
                <img src="/logo.png" alt="WinguPort" className="h-8 w-8" />
                <span className="text-lg font-bold hidden sm:block">
                    <span className="text-blue-300">Wingu</span>
                    <span className="text-white">Port</span>
                </span>
            </button>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Notifications */}
            <button
                onClick={() => navigate("/notifications")}
                className="relative p-2 text-blue-200 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                title="Notifications"
            >
                <Bell size={20} />
            </button>

            {/* Profile dropdown */}
            <div className="relative" ref={profileRef}>
                <button
                    onClick={() => setProfileOpen(!profileOpen)}
                    className="flex items-center gap-2 p-1.5 rounded-full hover:bg-white/10 transition-colors"
                >
                    {/* Avatar */}
                    <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-white/30 flex-shrink-0">
                        {avatarSrc ? (
                            <img src={avatarSrc} alt="avatar" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full bg-[#1e3a5f] flex items-center justify-center text-white text-sm font-bold">
                                {getDisplayAvatarChar(user)}
                            </div>
                        )}
                    </div>
                    <span className="hidden md:block text-sm font-semibold text-blue-100 max-w-[120px] truncate">
                        {displayName || "Profile"}
                    </span>
                    <ChevronDown
                        size={14}
                        className={`text-blue-300 transition-transform duration-200 ${profileOpen ? "rotate-180" : ""}`}
                    />
                </button>

                {/* Dropdown */}
                {profileOpen && (
                    <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50 animate-in fade-in-5 zoom-in-95">
                        <div className="px-4 py-2 border-b border-gray-100 mb-1">
                            <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
                            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        </div>

                        <button
                            onClick={() => { navigate(profileLink); setProfileOpen(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            {role === "recruiter" ? <Building size={15} className="text-gray-400" /> : <User size={15} className="text-gray-400" />}
                            {role === "recruiter" ? "Company Profile" : "My Profile"}
                        </button>

                        <button
                            onClick={() => { navigate("/account-settings"); setProfileOpen(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            <Settings size={15} className="text-gray-400" />
                            Account Settings
                        </button>

                        <div className="border-t border-gray-100 mt-1 pt-1">
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                            >
                                <LogOut size={15} className="text-red-400" />
                                Sign Out
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
};

// ─── Layout wrapper ───────────────────────────────────────────────────────────
const DashboardLayout = () => {
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const user = useAuthStore(s => s.user)

    return (
        <div className="min-h-screen bg-secondary-50">
            {/* Fixed top bar */}
            <DashboardTopBar onMobileMenuToggle={() => setIsMobileOpen(true)} />

            {/* Sidebar (desktop: fixed top-14..bottom; mobile: drawer) */}
            <DashboardSidebar
                isMobileOpen={isMobileOpen}
                onMobileClose={() => setIsMobileOpen(false)}
            />

            {/* Main content — top offset for topbar, left offset for sidebar */}
            <main className="lg:pl-[88px] pt-14 min-h-screen">
                <div className="p-4 md:p-6">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default DashboardLayout;
